import { inject, observer, Provider } from 'mobx-react';
import { parse } from '@AppModels/QueryStringUtils';
import * as React from 'react';
import { Component, useCallback, useState } from 'react';
import { useLocation, withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { IErrorMessageComponentProps, IUserSessionComponentProps } from '../../../models/AppState';
import { useEventLogging } from '../../../models/Logging';
import { CoffeeDrawerProvider } from '../../../web/components-v2/Drawer/AppDrawer';
import { Queue, QueueLoading } from '../../components/Queue';
import { DealModalProvider } from '../../contexts/dealModal';
import { EmailEditorProvider } from '../../contexts/email';
import { QueueLeadNoteProvider } from '../../contexts/queueLeadNote';
import { TysonModeContextValue, useTysonMode } from '../../contexts/tysonMode';
import { ErrorMessagesViewModelKey, UserSessionViewModelKey } from '../../models/AppState';
import { QueueState, QueueViewModelKey } from '../../models/QueueState';
import { ICreateQueueParams, QueueViewModel } from '../../viewModels/queue';

interface IProps extends IErrorMessageComponentProps, IUserSessionComponentProps, RouteComponentProps {
	tysonMode?: TysonModeContextValue;
}

interface IState {
	isLoading?: boolean;
	currentSearch?: ICreateQueueParams;
}

class QueueContainerBase extends Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		const { history } = props;
		const search = parse(history.location.search) as ICreateQueueParams;
		this.state = {
			currentSearch: search,
			isLoading: true,
		};
		QueueState[QueueViewModelKey] = new QueueViewModel(props.userSession, search);
	}

	public componentDidUpdate() {
		const { currentSearch } = this.state;
		const { errorMessages, history } = this.props;
		const search = parse(history.location.search) as ICreateQueueParams;
		if (search?.companyId !== currentSearch?.companyId) {
			this.setState({ currentSearch: search }, () => {
				QueueState[QueueViewModelKey].setLead(search).catch(err => {
					errorMessages.pushApiError(err);
				});
			});
		}
	}

	public componentDidMount() {
		const { errorMessages, tysonMode } = this.props;
		const search = parse(this.props.history.location.search) as ICreateQueueParams & { tysonMode?: string };
		const queue = QueueState[QueueViewModelKey];

		// Check if Tyson mode should be activated from URL parameter
		if (search.tysonMode === 'true' && tysonMode && !tysonMode.isActive) {
			tysonMode.enterTysonMode();
		}

		if (!search?.companyId) {
			// If Tyson mode is active, load practice lead instead of regular lead
			if (tysonMode?.isActive || search.tysonMode === 'true') {
				queue
					.getNextPracticeLead({ ruleId: search?.ruleId })
					.then(() => {
						this.setState({ isLoading: false });
					})
					.catch(err => {
						errorMessages.pushApiError(err);
						this.setState({ isLoading: false });
					});
			} else {
				queue
					.getNextLead({ ruleId: search?.ruleId })
					.then(() => {
						this.setState({ isLoading: false });
					})
					.catch(err => {
						errorMessages.pushApiError(err);
						this.setState({ isLoading: false });
					});
			}
		} else {
			this.setState({ isLoading: false });
		}
	}

	public render() {
		return <Provider {...QueueState}>{this.state.isLoading ? <QueueLoading /> : <QueueContainerContent />}</Provider>;
	}
}

// Separate functional component to manage CoffeeDrawer state
const QueueContainerContent: React.FC = () => {
	const [selectedDrawerValue, setSelectedDrawerValue] = useState('');

	const handleDrawerValueChange = useCallback((newValue: string) => {
		setSelectedDrawerValue(newValue);
	}, []);

	return (
		<CoffeeDrawerProvider value={selectedDrawerValue} onValueChange={handleDrawerValueChange}>
			<EmailEditorProvider>
				<DealModalProvider>
					<QueueLeadNoteProvider>
						<Queue />
					</QueueLeadNoteProvider>
				</DealModalProvider>
			</EmailEditorProvider>
		</CoffeeDrawerProvider>
	);
};

const QueueContainerObserver = observer(QueueContainerBase);
const QueueContainerWithRouter = withRouter(QueueContainerObserver);
const QueueContainerWithInjection = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey
)(QueueContainerWithRouter);

// Functional wrapper to provide TysonMode context to the class component
const QueueContainerWithTysonMode: React.FC = props => {
	const tysonMode = useTysonMode();
	const { pathname } = useLocation();
	const logger = useEventLogging('QueueContainer');
	React.useEffect(() => {
		logger.logPageView(pathname);
	}, [logger, pathname]);
	return <QueueContainerWithInjection {...props} tysonMode={tysonMode} />;
};

export const QueueContainer = QueueContainerWithTysonMode;
